import { inject, Injectable } from '@angular/core';
import { AlbumActions } from '@hunting-nature/Secacam/pages/gallery/albums/redux/album.actions';
import { FilterActions } from '@hunting-nature/Secacam/pages/gallery/image-gallery/filter/redux/filter.actions';
import { FilterService } from '@hunting-nature/Secacam/pages/gallery/image-gallery/filter/services/filter.service';
import { GalleryService } from '@hunting-nature/Secacam/pages/gallery/image-gallery/services/gallery.service';
import { NavController, ToastController } from '@ionic/angular';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ToastService } from '../../../../shared/services/toast.service';
import { CreateCustomAlbumActions } from './create-custom-album.actions';

@Injectable()
export class CreateCustomAlbumEffects {
  // services
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly navController = inject(NavController);
  private readonly galleryService = inject(GalleryService);
  private readonly toastService = inject(ToastService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly filterService = inject(FilterService);

  private showErrorToast(translationKey: string): void {
    const message = this.transloco.translate(translationKey);
    from(
      this.toastController.create({
        message,
        position: 'bottom',
        duration: 2000,
        color: 'danger',
      })
    )
      .pipe(tap(toast => toast.present()))
      .subscribe();
  }

  openCreateCustomAlbum$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateCustomAlbumActions.openCreateCustomAlbum),
        tap(() => {
          void this.navController.navigateForward(['/home/gallery/create-album']);
        })
      ),
    { dispatch: false }
  );

  createAlbum$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateCustomAlbumActions.createAlbum),
      switchMap(({ name, filters }) => {
        const filter = this.filterService.convertToApiFormat(filters);
        return this.galleryService.createAlbum$(name, filter).pipe(
          map(album => CreateCustomAlbumActions.createAlbumSucceeded({ album })),
          catchError((error: unknown) => {
            return of(CreateCustomAlbumActions.createAlbumFailed({ error }));
          })
        );
      })
    )
  );

  createAlbumSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateCustomAlbumActions.createAlbumSucceeded),
        tap(({ album }) => {
          this.toastService.showTranslatedInfo('gallery.createCustomAlbum.success');
          this.store.dispatch(AlbumActions.customAlbumCreated({ album }));
          this.store.dispatch(CreateCustomAlbumActions.closeCreateCustomAlbum());
        })
      ),
    { dispatch: false }
  );

  closeCreateAlbum$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateCustomAlbumActions.closeCreateCustomAlbum),
        tap(() => {
          this.store.dispatch(CreateCustomAlbumActions.clearAlbumNameInput());
          this.store.dispatch(FilterActions.clearAll());
          void this.navController.navigateBack('/home/gallery');
        })
      ),
    { dispatch: false }
  );

  handleError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CreateCustomAlbumActions.createAlbumFailed),
        tap(({ error }) => {
          const errorMessage = (error as any).error?.title || 'gallery.createCustomAlbum.error';
          this.showErrorToast(errorMessage);
        })
      ),
    { dispatch: false }
  );
}
